import {
  ReactNode,
  ComponentProps,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { Box, keyframes, styled } from '@applatform/design-system';
import { zIndices } from 'theme/constants';
import Icon from 'components/V1/Icon/Icon';
import { ReactComponent as CloseIcon } from 'icons/x.svg';

const VIEWPORT_PADDING = 20;
const INTERCOM_BUTTON_SIZE = 60;
const BOTTOM_RIGHT_PADDING = VIEWPORT_PADDING * 2 + INTERCOM_BUTTON_SIZE;

const hide = keyframes({
  '0%': { opacity: 1 },
  '100%': { opacity: 0 },
});

const slideIn = keyframes({
  from: { transform: `translateX(calc(100% + ${BOTTOM_RIGHT_PADDING}px))` },
  to: { transform: 'translateX(0)' },
});

const swipeOut = keyframes({
  from: { transform: 'translateX(var(--radix-toast-swipe-end-x))' },
  to: { transform: `translateX(calc(100% + ${BOTTOM_RIGHT_PADDING}px))` },
});

export const ToastViewport = styled(ToastPrimitive.Viewport, {
  position: 'fixed',
  bottom: 0,
  right: 0,
  display: 'flex',
  flexDirection: 'column',
  py: VIEWPORT_PADDING,
  pl: VIEWPORT_PADDING,
  pr: BOTTOM_RIGHT_PADDING,
  gap: 10,
  width: 560,
  maxWidth: '100vw',
  margin: 0,
  listStyle: 'none',
  zIndex: zIndices.toast,
});

const StyledToast = styled(ToastPrimitive.Root, {
  display: 'grid',
  gridTemplateAreas: '"icon description close"',
  gridTemplateColumns: 'max-content auto max-content',
  alignItems: 'center',
  px: '$4',
  py: '$3',
  columnGap: '$4',
  backgroundColor: '$white',
  borderTopRightRadius: '$sm',
  borderBottomRightRadius: '$sm',
  borderLeft: '4px solid currentColor',
  boxShadow:
    'hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px',

  '@media (prefers-reduced-motion: no-preference)': {
    '&[data-state="open"]': {
      animation: `${slideIn} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
    },
    '&[data-state="closed"]': {
      animation: `${hide} 100ms ease-in forwards`,
    },
    '&[data-swipe="move"]': {
      transform: 'translateX(var(--radix-toast-swipe-move-x))',
    },
    '&[data-swipe="cancel"]': {
      transform: 'translateX(0)',
      transition: 'transform 200ms ease-out',
    },
    '&[data-swipe="end"]': {
      animation: `${swipeOut} 100ms ease-out forwards`,
    },
  },

  variants: {
    status: {
      info: {
        color: '$violet900',
      },
      warning: {
        color: '$orange900',
      },
      error: {
        color: '$red900',
      },
      success: {
        color: '$green700',
      },
    },
  },
});

export const ToastDescription = styled(ToastPrimitive.Description, {
  gridArea: 'description',
  fontSize: '$xs',
  color: '$bodyGrey',
});

export const ToastIcon = styled(Box, {
  gridArea: 'icon',
  display: 'flex',
  alignItems: 'center',
  svg: {
    width: 20,
    height: 20,
  },
});

export const ToastClose = styled(ToastPrimitive.Close, {
  all: 'unset',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  gridArea: 'close',
  color: '$secondaryGrey',
  '&:hover': {
    color: '$secondary',
  },
});

interface ToastOwnProps {
  icon?: ReactNode;
}

type ToastProps = ComponentProps<typeof StyledToast> & ToastOwnProps;

export const Toast = ({ children, icon, ...props }: ToastProps) => {
  return (
    <StyledToast {...props}>
      <ToastIcon aria-hidden="true">{icon}</ToastIcon>
      <ToastDescription>{children}</ToastDescription>
      <ToastClose aria-label="Close">
        <Icon as={CloseIcon} aria-hidden="true" />
      </ToastClose>
    </StyledToast>
  );
};

interface ToastContent {
  description: ReactNode;
  icon?: ReactNode;
  status: 'info' | 'warning' | 'success' | 'error';
}

type ToastContextValue = (content: ToastContent) => void;

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function ToastProvider({
  children,
  ...props
}: ToastPrimitive.ToastProviderProps) {
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [toast, setToast] = useState<ToastContent | undefined>(undefined);
  const timerRef = useRef(0);

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  function showToast(toast: ToastContent) {
    setIsToastOpen(false);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      setToast(toast);
      setIsToastOpen(true);
    }, 100);
  }

  return (
    <ToastPrimitive.Provider {...props}>
      <ToastContext.Provider value={showToast}>
        {children}
        <Toast
          open={isToastOpen}
          onOpenChange={setIsToastOpen}
          status={toast?.status}
          icon={toast?.icon}
        >
          {toast?.description}
        </Toast>
      </ToastContext.Provider>
      <ToastViewport />
    </ToastPrimitive.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

export const ToastAction = ToastPrimitive.Action;
